export const platformSystems = [
  {
    id: "workbench",
    product: "ecosystem.Ai",
    title: "Workbench",
    purpose:
      "The operator surface. A team configures projects, data, models, and deployment here, including H2O scoring, without living inside the runtime.",
  },
  {
    id: "server",
    product: "ecosystem.Ai",
    title: "Prediction server",
    purpose:
      "The control plane. Projects, deployments, the prediction-worker architecture, domain-catalog sync, and the model gateway the other apps call.",
  },
  {
    id: "runtime",
    product: "ecosystem.Ai",
    title: "Client Pulse Responder",
    purpose:
      "The scoring engine. It loads the campaign, runs pre-score, model, post-score, and reward plugins, and returns a ranked result while the person is still acting. The response is written back so the next score can learn.",
    links: [
      {
        href: "https://developer.ecosystem.ai/docs/runtime",
        label: "Runtime docs",
      },
      {
        href: "https://github.com/ecogenetic/ecosystem-runtime-localbuild",
        label: "Public runtime architecture",
      },
    ],
  },
  {
    id: "data-agents",
    product: "ecosystem.Ai",
    title: "Data agents",
    purpose:
      "Explain live campaign and runtime data. Reports, quality, and statistics over what the platform already stored. Same sign-in as the workbench.",
  },
  {
    id: "modeling",
    product: "EcosystemCode",
    title: "Modeling",
    purpose:
      "The EcosystemCode application. Words become validated UML, then a deterministic running baseline.",
    links: [{ href: "https://ecosystemcode.com", label: "ecosystemcode.com" }],
  },
  {
    id: "catalog",
    product: "EcosystemCode",
    title: "Domain catalog",
    purpose: "The governed business language the products consume.",
    links: [
      {
        href: "https://github.com/ecogenetic/ecosystem-domain-catalog",
        label: "Repository",
      },
      { href: "https://ontology.ecosystem.ai", label: "Live catalog" },
    ],
  },
  {
    id: "campaign-portfolio",
    product: "Agentic Campaigning",
    title: "Portfolio",
    purpose:
      "Health, runs, channels, and financial performance of the campaigns already in Workbench.",
  },
  {
    id: "campaign-approvals",
    product: "Agentic Campaigning",
    title: "Work and approvals",
    purpose:
      "The operational queue. Technical readiness is stamped, then a different person signs off before a campaign can launch.",
  },
  {
    id: "campaign-calendar",
    product: "Agentic Campaigning",
    title: "Campaigns and calendar",
    purpose: "Workbench campaigns, their lifecycle stages, and when they are scheduled to run.",
  },
  {
    id: "campaign-audiences",
    product: "Agentic Campaigning",
    title: "Audiences and content",
    purpose:
      "Membership from the data sources already in use, approved content, and the offers and messages a campaign can send.",
  },
  {
    id: "campaign-operations",
    product: "Agentic Campaigning",
    title: "Operations",
    purpose:
      "Launch only when readiness is green. Run logs, step progress, and channel activation stay in the campaign workspace.",
  },
  {
    id: "campaign-analytics",
    product: "Agentic Campaigning",
    title: "Analytics and AI",
    purpose:
      "Semantic measures of runs and results. The Campaign Agent can help fill a campaign. Read-only analysis explains performance and does not invent a KPI.",
    links: [
      {
        href: "https://ecosystem.ai/agentic-campaigns",
        label: "Agentic Campaigns",
      },
    ],
  },
] as const;

export const repos = [
  {
    name: "ecosystem-runtime-localbuild",
    group: "Runtime",
    why: "Public Pulse Responder: plugins, scoring, response tracking.",
    tech: "Java / Spring Boot",
    url: "https://github.com/ecogenetic/ecosystem-runtime-localbuild",
  },
  {
    name: "ecosystem-algorithms",
    group: "Algorithms",
    why: "Notebooks and prediction-story templates for server and runtime.",
    tech: "Python / notebooks",
    url: "https://github.com/ecogenetic/ecosystem-algorithms",
  },
  {
    name: "ecosystem-domain-catalog",
    group: "Ontology",
    why: "Seed OWL/SKOS/SHACL domains used by EcosystemCode.",
    tech: "Python / Turtle",
    url: "https://github.com/ecogenetic/ecosystem-domain-catalog",
  },
  {
    name: "ecosystem.ai",
    group: "Docs",
    why: "Source for developer.ecosystem.ai.",
    tech: "MDX",
    url: "https://github.com/ecogenetic/ecosystem.ai",
  },
  {
    name: "ecosystem",
    group: "Deploy",
    why: "Local install: Docker, keys, start scripts.",
    tech: "Shell",
    url: "https://github.com/ecogenetic/ecosystem",
  },
  {
    name: "ecosystem-cli",
    group: "Deploy",
    why: "CLI for platform and deployments.",
    tech: "Python",
    url: "https://github.com/ecogenetic/ecosystem-cli",
  },
  {
    name: "message-app-python",
    group: "Examples",
    why: "Runtime calls and personality-based messages.",
    tech: "Python",
    url: "https://github.com/ecogenetic/message-app-python",
  },
  {
    name: "kNNBandit",
    group: "Research lineage",
    why: "RecSys 2019 nearest-neighbor bandit fork — algorithm ancestry, not the product.",
    tech: "Java",
    url: "https://github.com/jayvanzyl/kNNBandit",
  },
] as const;

export const advisoryOffers = [
  {
    slug: "innovation-systems",
    title: "Innovation systems",
    audience: "Leadership teams who need a portfolio that is more than a list of projects.",
    through:
      "Innovation portfolios, shadow networks, and the difference between incremental, disruptive, and radical impact.",
    leaves: "A structured view of the portfolio and the ecosystem around it — not a slogan.",
    trigger: "When formal capability models exist but nothing actually moves.",
  },
  {
    slug: "network-analysis",
    title: "Network and ecosystem analysis",
    audience: "Product and strategy groups who suspect the useful signal is relational.",
    through:
      "What is a node, what is an edge, what evidence weights the edge, and what action follows.",
    leaves: "A graph-shaped decision, with limits stated — including when not to use the method.",
    trigger: "When lists and funnels hide co-occurrence, influence, or missing complements.",
  },
  {
    slug: "platform-strategy",
    title: "Platform strategy",
    audience: "Architects and executives deciding what belongs in a core.",
    through:
      "The lag between invention and an emergent platform. Commonality versus variability. Contracts, not forks.",
    leaves: "A boundary: what is platform, what is product, what should never be a tenant `if`.",
    trigger: "When every new line of business quietly forks the system.",
  },
  {
    slug: "digital-strategy",
    title: "Digital strategy and delivery",
    audience:
      "Leadership teams setting the digital direction, and the teams who have to turn that direction into a delivery project.",
    through:
      "Where the enterprise is going digitally, then machine learning, automation, business model, and a delivery shape that can be staffed. Much of the strategy is personalization, and the role of AI in that process.",
    leaves:
      "A strategy the backlog can recognize — including what will not be built yet.",
    trigger: "When the strategy deck and the delivery plan do not describe the same system.",
  },
  {
    slug: "human-organism-as-algorithm",
    title: "The Human Organism as Algorithm",
    audience: "Leaders and research groups who need a language for how people decide.",
    through:
      "Attention, decision, and an informational environment that is networked, mediated, and optimized by systems people do not control.",
    leaves:
      "A shared account of the human as a set of instructions — and where that changes a product, a policy, or a research question.",
    trigger: "When behavior is still treated as a click log.",
  },
  {
    slug: "new-world-of-work",
    title: "New world of work",
    audience:
      "People working through what a changing world asks of a person, a group, and a society.",
    through:
      "The individual, the collective, and society — and how AI contributes to success at each of those scales.",
    leaves:
      "A clear account of success for the person, the group, and the society, and the part AI plays in each.",
    trigger: "When the conversation stays inside a job title and misses the person, the group, and the society.",
  },
  {
    slug: "ai-first-enterprise",
    title: "The AI-first enterprise",
    audience: "Enterprises deciding to run on AI, not to park it beside the business.",
    through:
      "What has to be true for AI to be the operating default: a workbench, a runtime, a response loop, and an operator who owns the outcome.",
    leaves:
      "A boundary between an AI programme and an enterprise that can still run the prediction.",
    trigger: "When AI is a side office and the operating model has not moved.",
  },
  {
    slug: "intelligent-automation",
    title: "Intelligent automation",
    audience: "Especially financial-services operators.",
    through:
      "Three stages: basic machine intelligence, self-correcting learning, and augmented human-machine environments.",
    leaves: "A map of where humans should still operate, and where a runtime should score.",
    trigger: "When automation is a programme name rather than a closed loop.",
  },
] as const;
